import React, { useState } from 'react'
import { useAuth, useSubscriptionWithLoading } from '@talktoast/shared'
import { usePreferences } from '@talktoast/shared'
import { DEFAULT_PERSISTENT_PREFERENCES } from '@talktoast/shared/types/preferences'
import { Card, Button, Loading } from '@talktoast/ui'

interface SettingsHubProps {
  onNavigate: (destination: 'dietary' | 'appliances' | 'diet-preference' | 'session-prefs' | 'subscription-management') => void
  onBack: () => void
}

const CALORIE_MEAL_TYPES = [
  { type: 'breakfast' as const, label: 'Breakfast' },
  { type: 'lunch' as const, label: 'Lunch' },
  { type: 'dinner' as const, label: 'Dinner' },
  { type: 'snacks' as const, label: 'Snacks' },
  { type: 'dessert' as const, label: 'Dessert' },
]

// Icon components
const ICONS = {
  subscription: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
    </svg>
  ),
  account: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
  ),
  forward: (
    <svg className="w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
      <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
    </svg>
  )
}

export default function SettingsHub({ onNavigate, onBack }: SettingsHubProps) {
  const { user, isAuthenticated, signOut } = useAuth()
  const { preferences, loading, updatePersistentPreferences } = usePreferences()
  const { subscription, loading: subscriptionLoading, tier, isInTrial, hasData } = useSubscriptionWithLoading()
  const [savingCalories, setSavingCalories] = useState(false)
  const [calorieError, setCalorieError] = useState<string | null>(null)

  const persistentPrefs = preferences?.persistent
  const calorieTargeting = persistentPrefs?.calorieTargeting ?? DEFAULT_PERSISTENT_PREFERENCES.calorieTargeting

  // Local draft for calorie inputs — committed on blur to avoid per-keystroke saves
  const [calorieDraft, setCalorieDraft] = useState<Record<string, string>>({})

  const toggleCalorieTargeting = async () => {
    if (!persistentPrefs) return
    setSavingCalories(true)
    setCalorieError(null)
    try {
      await updatePersistentPreferences({
        ...persistentPrefs,
        calorieTargeting: { ...calorieTargeting, enabled: !calorieTargeting.enabled }
      })
    } catch {
      setCalorieError('Failed to save. Please try again.')
    } finally {
      setSavingCalories(false)
    }
  }

  const commitCalorieTarget = async (mealType: keyof typeof calorieTargeting.perMealTargets) => {
    if (!persistentPrefs) return
    const raw = calorieDraft[mealType]
    if (raw === undefined) return // no draft for this field
    const parsed = parseInt(raw, 10)
    const clamped = isNaN(parsed) ? calorieTargeting.perMealTargets[mealType] : Math.max(50, Math.min(2000, parsed))
    setCalorieDraft(prev => { const next = { ...prev }; delete next[mealType]; return next })
    setCalorieError(null)
    try {
      await updatePersistentPreferences({
        ...persistentPrefs,
        calorieTargeting: {
          ...calorieTargeting,
          perMealTargets: { ...calorieTargeting.perMealTargets, [mealType]: clamped }
        }
      })
    } catch {
      setCalorieError('Failed to save. Please try again.')
    }
  }

  // Show loading state while preferences are being fetched
  if (loading) {
    return (
      <div className="min-h-screen bg-white p-4 flex items-center justify-center">
        <div className="text-center">
          <Loading variant="spinner" size="lg" className="mx-auto mb-4" />
          <p className="text-gray-600">Loading settings...</p>
        </div>
      </div>
    )
  }

  const handleSignOut = async () => {
    if (window.confirm('Are you sure you want to sign out?')) {
      await signOut()
      onBack()
    }
  }

  return (
    <div className="min-h-screen bg-white p-4">
      <div className="max-w-md mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-1">Account</h1>
          <p className="text-sm text-gray-600">Manage your account and subscription</p>
        </div>

        {/* Account Info */}
        {isAuthenticated && (
          <div className="mb-4">
            <Card className="p-4 border-2 border-gray-200 mb-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center text-gray-600">
                  {ICONS.account}
                </div>
                <div>
                  <div className="text-xs text-gray-500">Signed in as</div>
                  <div className="text-sm font-medium text-gray-900">{user?.email}</div>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Subscription - Expanded Inline Display */}
        <div className="mb-4">
          {subscriptionLoading ? (
            // Loading skeleton
            <Card className="p-4 border-2 border-gray-200 mb-3">
              <div className="animate-pulse space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-gray-200"></div>
                  <div className="flex-1">
                    <div className="h-4 bg-gray-200 rounded w-24 mb-2"></div>
                    <div className="h-3 bg-gray-200 rounded w-16"></div>
                  </div>
                </div>
                <div className="h-10 bg-gray-200 rounded"></div>
              </div>
            </Card>
          ) : (
            // Loaded state
            <Card className="p-4 border-2 border-gray-200 mb-3">
              <div className="space-y-3">
                {/* Header */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-toast-gold-100 flex items-center justify-center text-toast-gold">
                      {ICONS.subscription}
                    </div>
                    <div>
                      <h3 className="text-base font-semibold text-gray-900">Subscription</h3>
                      <p className="text-xs text-gray-500">
                        {tier === 'premium' ? 'Premium Plan' : 'Free Plan'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Subscription Details - Only show if we have data */}
                {hasData && (
                  <div className="pl-13 space-y-2 text-sm">
                    {/* Status */}
                    {isInTrial && (
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Status</span>
                        <span className="px-2 py-0.5 bg-green-100 text-green-800 text-xs font-medium rounded-full">
                          Trial Active
                        </span>
                      </div>
                    )}

                    {/* Trial/Renewal Date */}
                    {isInTrial && subscription?.trialEnd && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Trial Ends</span>
                        <span className="text-gray-900 font-medium">
                          {new Date(subscription.trialEnd).toLocaleDateString()}
                        </span>
                      </div>
                    )}

                    {!isInTrial && subscription?.periodEnd && tier !== 'free' && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Renews</span>
                        <span className="text-gray-900 font-medium">
                          {new Date(subscription.periodEnd).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {/* Manage Button */}
                <button
                  onClick={() => onNavigate('subscription-management')}
                  className="w-full mt-2 py-2 px-3 text-sm text-toast-gold-700 bg-toast-gold-50 hover:bg-toast-gold-100 rounded-lg transition-colors font-medium"
                >
                  Manage Subscription
                </button>
              </div>
            </Card>
          )}
        </div>

        {/* Calorie Targeting */}
        <div className="mb-4">
          <Card className="p-4 border-2 border-gray-200">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-base font-semibold text-gray-900">Calorie targeting</h3>
                  <p className="text-xs text-gray-500">Set per-meal calorie goals</p>
                </div>
                <button
                  onClick={toggleCalorieTargeting}
                  disabled={savingCalories}
                  aria-label={`Calorie targeting: ${calorieTargeting.enabled ? 'on, click to turn off' : 'off, click to turn on'}`}
                  className={`text-xs px-3 py-1 rounded-full font-medium transition-colors disabled:opacity-50 ${
                    calorieTargeting.enabled
                      ? 'bg-toast-gold text-white'
                      : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                  }`}
                >
                  {calorieTargeting.enabled ? 'ON' : 'OFF'}
                </button>
              </div>

              {calorieError && (
                <p className="text-xs text-red-600">{calorieError}</p>
              )}

              {calorieTargeting.enabled && (
                <div className="space-y-2 pt-1">
                  {CALORIE_MEAL_TYPES.map(({ type, label }) => (
                    <div key={type} className="flex items-center justify-between">
                      <label htmlFor={`cal-${type}`} className="text-sm text-gray-700">{label}</label>
                      <div className="flex items-center gap-1.5">
                        <input
                          id={`cal-${type}`}
                          type="number"
                          value={calorieDraft[type] ?? calorieTargeting.perMealTargets[type]}
                          onChange={(e) => setCalorieDraft(prev => ({ ...prev, [type]: e.target.value }))}
                          onBlur={() => commitCalorieTarget(type)}
                          className="w-20 px-2 py-1 border border-gray-300 rounded text-sm text-right"
                          min={50}
                          max={2000}
                        />
                        <span className="text-xs text-gray-500 w-6">cal</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Card>
        </div>

        {/* Sign Out */}
        {isAuthenticated && (
          <div className="mb-6">
            <Button
              variant="outline"
              fullWidth
              onClick={handleSignOut}
              className="border-red-200 hover:border-red-500 hover:bg-red-50 text-red-600"
            >
              Sign Out
            </Button>
          </div>
        )}

        {/* Back Button */}
        <Button
          variant="secondary"
          fullWidth
          onClick={onBack}
        >
          ← Back to Home
        </Button>
      </div>
    </div>
  )
}
